import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import classNames from 'classnames'
import { useMediaQuery } from 'react-responsive'

import { isOwner, isSubscriptionExpired } from 'common/utils/permissions'
import { isBlockArticleType } from 'common/utils/isBlockArticleType'
import { getAvatarText } from 'common/utils/getAvatarText.js'
import DropdownMenu from 'components/DropdownMenu/DropdownMenu'
import InlineInput from 'components/Form/InlineInput/InlineInput'
import { TooltipWrapper } from 'components/Tooltip/Tooltip'
import {
    IconCopyLink,
    IconDelete,
    IconDuplicate,
    IconRename,
    TripleDotsIcon,
    IconVisible,
    IconHidden,
    IconHiddenBlue,
} from 'svg'

import { MODAL_TYPES } from '../../modals/ModalController'

import ProjectStatistic from './components/ProjectStatistic/ProjectStatistic'
import PreviewBlock from './components/PreviewBlock/PreviewBlock'
import ActionButtons from './components/ActionButtons/ActionButtons'
import ProjectUpdateInfo from './components/ProjectUpdateInfo/ProjectUpdateInfo'

import { mapMemberName } from '../../utils/mapper.js'

import { PROJECT_STATUS } from '../../utils/constants'

import './ProjectBlock.scss'

const ProjectBlock = ({
    project,
    onOpenModal,
    onEditProject,
    onOpenShareModal,
    onDuplicateProject,
    onRenameProject,
    onChangeProjectVisibility,
    actionLoading,
    isLoading = false,
}) => {
    const { t } = useTranslation()
    const isMobile = useMediaQuery({ query: '(max-width: 1340px)' })
    const {
        organizations: { selectedOrganizationId },
        user_details,
    } = useSelector(state => ({
        organizations: state.organizations,
        user_details: state.user_details,
    }))
    const { memberList } = useSelector(state => state.organizations)

    const isPublished = project.projectStatus === PROJECT_STATUS.published
    const [isUserOwnerOrAuthor, setIsUserOwnerOrAuthor] = useState(() => getIsUserOwnerOrAuthor())
    const [isUserOwnerAndAuthor, setIsUserOwnerAndAuthor] = useState(() => getIsUserOwnerAndAuthor())
    const [isEditableName, seIsEditableName] = useState(false)

    useEffect(() => {
        setIsUserOwnerOrAuthor(getIsUserOwnerOrAuthor())
        setIsUserOwnerAndAuthor(getIsUserOwnerAndAuthor())
    }, [selectedOrganizationId, project, user_details])

    function getIsUserOwnerOrAuthor() {
        return isOwner() || project.author.userId === user_details.id
    }
    function getIsUserOwnerAndAuthor() {
        return isOwner() && project.author.userId === user_details.id
    }

    const onSaveRename = val => {
        if (val !== project.name) {
            onRenameProject(project, val)
        }
        seIsEditableName(false)
    }

    const dropdownMenu = useMemo(() => {
        if (isLoading) return null

        const actionMenuItems = [
            isPublished && (
                <p className="project-block__dropdown-menu-item" onClick={evt => onOpenShareModal(project, evt)}>
                    <span className="project-block__dropdown-menu-icon">
                        <IconCopyLink />
                    </span>
                    {t('Copy link')}
                </p>
            ),
            isBlockArticleType(project.projectType.projectType) && (
                <p
                    className={`project-block__dropdown-menu-item ${
                        actionLoading.duplicateProject ? 'is-loading' : ''
                    }`}
                    onClick={evt => onDuplicateProject(project, evt)}
                >
                    <span className="project-block__dropdown-menu-icon">
                        <IconDuplicate />
                    </span>
                    {t('Clone project')}
                </p>
            ),
            isUserOwnerOrAuthor && (
                <p
                    className="project-block__dropdown-menu-item"
                    onClick={
                        isSubscriptionExpired()
                            ? evt => onOpenModal(MODAL_TYPES.subscriptionExpired, project, evt)
                            : () => seIsEditableName(true)
                    }
                >
                    <span className="project-block__dropdown-menu-icon">
                        <IconRename />
                    </span>
                    {t('Rename')}
                </p>
            ),
            memberList.length > 1 &&
                isUserOwnerAndAuthor &&
                (project.hidden ? (
                    <p
                        className="project-block__dropdown-menu-item"
                        onClick={() => onChangeProjectVisibility(project, false)}
                    >
                        <span className="project-block__dropdown-menu-icon">
                            <IconVisible />
                        </span>
                        {t('Make visible to the team')}
                    </p>
                ) : (
                    <p
                        className="project-block__dropdown-menu-item"
                        onClick={() => onChangeProjectVisibility(project, true)}
                    >
                        <span className="project-block__dropdown-menu-icon">
                            <IconHidden />
                        </span>
                        {t('Hide from the team')}
                    </p>
                )),
            isUserOwnerOrAuthor && (
                <p
                    className="project-block__dropdown-menu-item"
                    onClick={evt => onOpenModal(MODAL_TYPES.deleteProjectModal, project, evt)}
                >
                    <span className="project-block__dropdown-menu-icon">
                        <IconDelete />
                    </span>
                    {t('Delete')}
                </p>
            ),
        ]

        return (
            <div className="project-block__top-right-box">
                {memberList.length > 1 && project.hidden && (
                    <div className="project-block__visibility-icon">
                        <TooltipWrapper
                            tooltipText={t(
                                'This project is hidden from the team members. Go to the menu under the three dots to make it visible to the team.',
                            )}
                        >
                            <span>
                                <IconHiddenBlue />
                            </span>
                        </TooltipWrapper>
                    </div>
                )}
                <DropdownMenu
                    className="project-block__dropdown-menu us-n"
                    placement={'bottomRight'}
                    actionHtml={<TripleDotsIcon />}
                    items={actionMenuItems}
                />
            </div>
        )
    }, [
        memberList,
        project,
        onOpenModal,
        onOpenShareModal,
        onDuplicateProject,
        actionLoading,
        isUserOwnerOrAuthor,
        isUserOwnerAndAuthor,
        isLoading,
    ])

    return (
        <li
            className={classNames({
                'project-block': true,
                'is-removed': project.isRemoved,
                'is-blocked': project.projectBlockStatus === 'BLOCKED',
            })}
            data-deleted-text={t('deleted')}
            data-blocked-text={t('blocked')}
        >
            {!isMobile && (
                <div className="project-block__desktop-view">
                    <PreviewBlock project={project} isLoading={isLoading} />
                    <div className="project-block__desktop-view-block">
                        <div className="project-block__desktop-view-info">
                            <div className="project-block__desktop-view-info-header">
                                <div className="project-block__desktop-view-info-title" title={project.name}>
                                    <InlineInput
                                        value={project.name}
                                        maxLength={128}
                                        isEditable={isEditableName}
                                        onSave={val => onSaveRename(val)}
                                        isLoading={isLoading}
                                    />
                                </div>

                                {!isLoading && (
                                    <div className="project-block__author-info">
                                        {project.author.avatarUrl ? (
                                            <img
                                                className="project-block__author-info-icon"
                                                src={project.author.avatarUrl}
                                            />
                                        ) : (
                                            <div className="project-block__author-info-icon">
                                                {getAvatarText(project.author)}
                                            </div>
                                        )}
                                        <p className="project-block__author-info-title">
                                            {mapMemberName(project.author)}
                                        </p>
                                    </div>
                                )}
                            </div>
                            {!isLoading && isPublished ? <ProjectStatistic item={project} /> : <div />}
                        </div>
                        <div className="project-block__desktop-view-actions">
                            <ProjectUpdateInfo project={project} isPublished={isPublished} isLoading={isLoading} />
                            <ActionButtons
                                isPublished={isPublished}
                                project={project}
                                onOpenModal={onOpenModal}
                                onDuplicateProject={onDuplicateProject}
                                onEditProject={onEditProject}
                                isCanManageProject={isUserOwnerOrAuthor}
                                isLoading={isLoading}
                            />
                        </div>
                    </div>
                    {dropdownMenu}
                </div>
            )}

            {isMobile && (
                <div className="project-block__mobile-view">
                    <div className="project-block__mobile-view-header">
                        <div className="project-block__mobile-view-title" title={project.name}>
                            <InlineInput
                                value={project.name}
                                maxLength={128}
                                isEditable={isEditableName}
                                onSave={val => onSaveRename(val)}
                                isLoading={isLoading}
                            />
                        </div>
                        {!isLoading && (
                            <div className="project-block__author-info">
                                {project.author.avatarUrl ? (
                                    <img className="project-block__author-info-icon" src={project.author.avatarUrl} />
                                ) : (
                                    <div className="project-block__author-info-icon">
                                        {getAvatarText(project.author)}
                                    </div>
                                )}
                                <p className="project-block__author-info-title">{mapMemberName(project.author)}</p>
                            </div>
                        )}
                        {dropdownMenu}
                    </div>
                    <PreviewBlock project={project} isLoading={isLoading} />
                    {!isLoading && isPublished && <ProjectStatistic item={project} />}

                    <ActionButtons
                        isPublished={isPublished}
                        project={project}
                        onOpenModal={onOpenModal}
                        onDuplicateProject={onDuplicateProject}
                        onEditProject={onEditProject}
                        isCanManageProject={isUserOwnerOrAuthor}
                        isLoading={isLoading}
                    />
                </div>
            )}
        </li>
    )
}

export default ProjectBlock
